<template>
  <div class="mobile-workflow-container" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd">

    <!-- Back Button (Top Left) -->
    <div class="back-button">
      <v-btn
        color="grey darken-2"
        dark
        small
        fab
        @click="$emit('go-back')"
      >
        <v-icon small>mdi-arrow-left</v-icon>
      </v-btn>
    </div>

    <!-- Fullscreen Toggle (Below Back Button) -->
    <div v-if="fullscreenSupported" class="fullscreen-toggle">
      <v-btn
        color="blue"
        dark
        small
        fab
        @click="isFullscreen ? exitFullscreen() : enterFullscreen()"
      >
        <v-icon small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
      </v-btn>
    </div>

    <!-- Printer Button (Below Fullscreen) -->
    <div class="printer-button">
      <v-btn
        color="primary"
        dark
        small
        fab
        @click="$emit('open-settings-modal')"
      >
        <v-icon small>mdi-printer</v-icon>
      </v-btn>
    </div>

    <!-- Problem Button (Below Printer) - Only show during picking (step 1) -->
    <div v-if="step === 1" class="problem-button">
      <v-btn
        color="red"
        dark
        small
        fab
        @click="$emit('open-problem-modal')"
      >
        <v-icon small>mdi-alert-circle</v-icon>
      </v-btn>
    </div>

    <!-- Full-Width Status Bar - Now for all steps -->
    <div class="status-bar" :class="getStatusBarClass()">
      <div class="status-bar-content">
        <div class="status-info">
          <v-icon color="white" size="20" class="status-icon">{{ getStatusIcon() }}</v-icon>
          <span class="status-text">{{ getStatusText() }}</span>
          <span v-if="currentSlot && (step === 1 || step === 3)" class="status-slot">{{ currentSlot }}</span>
        </div>
        <div class="status-progress" v-if="getStatusProgress()">
          <span class="progress-text">{{ getStatusProgress() }}</span>
        </div>
      </div>
    </div>

    <!-- Debug Console Panel (Only shown when debug mode is active) -->
    <div v-if="debugModeActive" class="debug-console-panel">
      <div class="debug-console-header">
        <div class="debug-header-left">
          <v-icon small color="white" @click="toggleDebugConsole" style="cursor: pointer;">
            {{ debugConsoleExpanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
          </v-icon>
          <span @click="toggleDebugConsole" style="cursor: pointer;">Debug Console ({{ debugLogs.length }})</span>
        </div>
        <v-btn x-small color="error" dark @click="disableDebugMode">
          <v-icon x-small left>mdi-close</v-icon>
          Disable
        </v-btn>
      </div>
      <div v-if="debugConsoleExpanded" class="debug-console-content">
        <div v-for="(log, index) in debugLogs" :key="index" class="debug-log-entry" :class="log.type">
          <span class="log-time">{{ log.time }}</span>
          <span class="log-message">{{ log.message }}</span>
        </div>
      </div>
    </div>

    <!-- Order ID Badge - Only show during picking (step 1) -->
    <div v-if="step === 1" class="order-id-badge">
      <v-chip color="primary" dark small class="order-chip">
        <v-icon left x-small>mdi-package-variant</v-icon>
        <span class="order-id-text">{{ orderId }}</span>
      </v-chip>
    </div>

    <!-- Step 1: Picking Phase -->
    <div v-if="step === 1" class="workflow-step">
      <!-- Current Article Card -->
      <div v-if="currentArticle" class="article-card" :class="{ 'scanned': isArticleScannedInPicking(currentArticle), 'dark-mode': isDarkMode }">
        <div class="article-sku">{{ currentArticle.sku }}</div>
        <div v-if="getImageUrl(currentArticle)" class="article-preview" @click="toggleDarkMode">
          <img :src="getImageUrl(currentArticle)" :alt="currentArticle.sku" />
          <!-- Dark Mode Indicator -->
          <div v-if="isDarkMode" class="dark-mode-indicator">
            <v-icon small color="white">mdi-weather-night</v-icon>
          </div>
        </div>
        <!-- No Image: Show line1 from translated object -->
        <div v-else class="article-preview no-image" @click="toggleDarkMode">
          <div class="article-text-fallback">
            {{ currentArticle.translated?.line1 || 'Kein Bild verfügbar' }}
          </div>
          <!-- Dark Mode Indicator -->
          <div v-if="isDarkMode" class="dark-mode-indicator">
            <v-icon small color="white">mdi-weather-night</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: Walk to Table -->
    <div v-if="step === 2" class="workflow-step walk-to-table-step">
      <div class="minimal-header">
        <span class="status-label">Zum Packtisch</span>
      </div>

      <!-- Walk to Table Image -->
      <div class="walk-image-container">
        <img :src="tableWalkImage" alt="Walk to Table" class="walk-image" />
      </div>

      <!-- Text and Button -->
      <div class="walk-content">
        <p class="walk-text">Gehe mit den Artikeln zum Packtisch</p>
        <v-btn x-large color="primary" block @click="$emit('advance-step')" class="walk-button">
          <v-icon left>mdi-check-bold</v-icon>
          Bereit
        </v-btn>
      </div>
    </div>

    <!-- Step 3: Packing Verification -->
    <div v-if="step === 3" class="workflow-step">
      <!-- Current Article Card -->
      <div v-if="currentArticleForPacking" class="article-card" :class="{ 'scanned': isArticleScannedInPacking(currentArticleForPacking), 'dark-mode': isDarkMode }">
        <div class="article-sku">{{ currentArticleForPacking.sku }}</div>
        <div v-if="getImageUrl(currentArticleForPacking)" class="article-preview" @click="toggleDarkMode">
          <img :src="getImageUrl(currentArticleForPacking)" :alt="currentArticleForPacking.sku" />
          <!-- Dark Mode Indicator -->
          <div v-if="isDarkMode" class="dark-mode-indicator">
            <v-icon small color="white">mdi-weather-night</v-icon>
          </div>
        </div>
        <!-- No Image: Show line1 from translated object -->
        <div v-else class="article-preview no-image" @click="toggleDarkMode">
          <div class="article-text-fallback">
            {{ currentArticleForPacking.translated?.line1 || 'Kein Bild verfügbar' }}
          </div>
          <!-- Dark Mode Indicator -->
          <div v-if="isDarkMode" class="dark-mode-indicator">
            <v-icon small color="white">mdi-weather-night</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 4: Carton Confirmation -->
    <div v-if="step === 4 && packaging" class="workflow-step carton-step">
      <!-- Carton Display - Larger card, Dynamic Height -->
      <div class="carton-display-container">
        <div class="carton-large-card">
          <!-- Carton Image or Icon -->
          <div class="carton-image-wrapper">
            <img
              v-if="packaging.img"
              :src="packaging.img"
              :alt="packaging.name || 'Karton'"
              class="carton-large-image"
            />
            <v-icon v-else size="80" color="#795548">mdi-package-variant</v-icon>
          </div>

          <!-- Carton Info -->
          <div class="carton-large-info">
            <div class="carton-large-name">{{ packaging.name || packaging.sku || 'Karton' }}</div>
            <div class="carton-large-sku">{{ packaging.sku }}</div>
            <div v-if="packaging.dimensions" class="carton-large-dimensions">
              {{ packaging.dimensions }}
            </div>
          </div>
        </div>
      </div>

      <!-- Confirm Button -->
      <v-btn
        x-large
        color="primary"
        block
        @click="$emit('confirm-carton')"
        class="confirm-btn"
      >
        <v-icon left>mdi-check-bold</v-icon>
        Karton bestätigt
      </v-btn>
    </div>

    <!-- Step 5: Upsell Items Confirmation (One by One) -->
    <div v-if="step === 5 && currentUpsellItem" class="workflow-step upsell-step">
      <!-- Single Upsell Item Display - Larger, Dynamic Height -->
      <div class="upsell-display-container">
        <div class="upsell-large-card">
          <img
            v-if="getUpsellImageUrl(currentUpsellItem.sku)"
            :src="getUpsellImageUrl(currentUpsellItem.sku)"
            :alt="currentUpsellItem.sku"
            class="upsell-large-image"
          />
          <v-icon v-else size="120" color="grey">mdi-package-variant</v-icon>
          <div v-if="currentUpsellItem.qty > 1" class="qty-badge-large">{{ currentUpsellItem.qty }}x</div>
        </div>
        <div class="upsell-large-badge">
          {{ currentUpsellItem.sku }}
        </div>
      </div>

      <!-- Confirm Button -->
      <v-btn
        x-large
        color="primary"
        block
        @click="handleUpsellConfirm"
        class="confirm-btn"
      >
        <v-icon left>mdi-check-bold</v-icon>
        {{ currentUpsellIndex < upsellItems.length - 1 ? 'Nächster Upsell' : 'Upsell eingelegt' }}
      </v-btn>
    </div>

    <!-- Step 6: Flyer Display (One by One) -->
    <div v-if="step === 6 && currentFlyerItem" class="workflow-step flyer-step-container">
      <!-- Single Flyer Display - Larger, Dynamic Height -->
      <div class="flyer-display-container">
        <div class="flyer-large-card" :style="{ backgroundColor: currentFlyerItem.bg }">
          <img :src="currentFlyerItem.url" :alt="currentFlyerItem.label" class="flyer-large-image" />
        </div>
        <div class="flyer-large-badge">
          FLYER {{ currentFlyers.length > 1 ? (currentFlyerIndex + 1) : '' }}
        </div>
      </div>

      <!-- Confirm Button -->
      <v-btn
        x-large
        color="primary"
        block
        @click="handleFlyerConfirm"
        class="confirm-flyer-btn"
      >
        <v-icon left>mdi-check-bold</v-icon>
        {{ currentFlyerIndex < currentFlyers.length - 1 ? 'Nächster Flyer' : 'Flyer eingelegt' }}
      </v-btn>
    </div>

    <!-- Step 7: Label Scan -->
    <div v-if="step === 7" class="workflow-step label-scan-step">
      <!-- Background Label Image -->
      <div class="label-background-container" :key="'label-container-' + step + '-' + currentLabelType">
        <!-- Show Swiss A4 label OR shipping label based on currentLabelType -->
        <img
          v-if="currentLabelType === 'swiss'"
          :src="swissLabelImage"
          :key="'swiss-a4-' + step"
          class="label-background-image label-image-a4"
          alt="Swiss Document"
        />
        <img
          v-else
          :src="shippingLabelImage"
          :key="'shipping-label-' + step"
          class="label-background-image label-image-shipping"
          alt="Shipping Label"
        />
      </div>

      <!-- Controls Overlay at Bottom -->
      <div class="label-controls-overlay">
        <div class="scan-message">
          <v-icon size="64" :color="trackingScanned && currentLabelType === 'shipping' ? 'success' : 'primary'">
            {{ trackingScanned && currentLabelType === 'shipping' ? 'mdi-check-circle' : 'mdi-barcode-scan' }}
          </v-icon>
          <p v-if="currentLabelType === 'swiss'">Scanne Swiss Dokument</p>
          <p v-else-if="!trackingScanned">Scanne Versandlabel</p>
          <p v-else>Versandlabel gescannt!</p>
        </div>

        <!-- Reprint Label Button -->
        <v-btn
          large
          color="orange"
          outlined
          @click="handleReprintLabel"
          class="reprint-label-btn"
        >
          <v-icon left>mdi-printer</v-icon>
          {{ currentLabelType === 'swiss' ? 'A4 Dokumente erneut drucken' : 'Label erneut drucken' }}
        </v-btn>
      </div>
    </div>

    <!-- Step 8: Additional Tracking (Switzerland) -->
    <div v-if="step === 8" class="workflow-step additional-label-step">
      <!-- Background Label Image -->
      <div class="label-background-container" :key="'label-container-' + step + '-' + currentLabelType">
        <img
          v-if="shippingLabelUrls && shippingLabelUrls.length > 0"
          :src="shippingLabelImage"
          :key="'shipping-label-' + step"
          class="label-background-image label-image-shipping"
          alt="Shipping Label"
        />
      </div>

      <!-- Controls Overlay at Bottom -->
      <div class="label-controls-overlay">
        <div class="scan-message">
          <v-icon size="64" color="primary">mdi-barcode-scan</v-icon>
          <p>Scanne zweites Label</p>
        </div>

        <!-- Reprint Label Button for Step 8 -->
        <v-btn
          large
          color="orange"
          outlined
          @click="handleReprintShippingLabel"
          class="reprint-label-btn"
        >
          <v-icon left>mdi-printer</v-icon>
          Versandlabel erneut drucken
        </v-btn>
      </div>
    </div>

    <!-- Step 9: Complete -->
    <div v-if="step === 9" class="workflow-step">
      <div class="minimal-header">
        <span class="status-label">Fertig</span>
      </div>
      <div class="complete-message">
        <v-icon size="64" color="success">mdi-check-circle</v-icon>
        <p>Alle Schritte abgeschlossen</p>
        <v-btn large color="success" @click="$emit('pack')">
          Verpacken
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import tableWalkImage from '@/assets/files/tableWalk.png';

export default {
  name: 'MobileWorkflowView',
  data() {
    return {
      showToolbar: false,
      touchStartY: 0,
      touchDelta: 0,
      currentUpsellIndex: 0,
      currentFlyerIndex: 0,
      tableWalkImage: tableWalkImage,
      // Fullscreen support
      isFullscreen: false,
      fullscreenSupported: false,
      // Debug console (admin only)
      debugModeActive: false,
      debugConsoleExpanded: true, // Expand by default when activated
      debugLogs: [],
      // Dark mode for printfiles
      isDarkMode: false
    };
  },
  props: {
    step: {
      type: Number,
      required: true
    },
    orderId: {
      type: String,
      default: null
    },
    articles: {
      type: Array,
      default: () => []
    },
    currentSlot: {
      type: String,
      default: null
    },
    progress: {
      type: Object,
      default: () => ({ scanned: 0, total: 0 })
    },
    packingProgress: {
      type: Object,
      default: () => ({ scanned: 0, total: 0 })
    },
    pickingComplete: {
      type: Boolean,
      default: false
    },
    packingComplete: {
      type: Boolean,
      default: false
    },
    tracking: {
      type: String,
      default: null
    },
    trackingScanned: {
      type: Boolean,
      default: false
    },
    scannedTrackingIds: {
      type: Array,
      default: () => []
    },
    remainingTrackingIds: {
      type: Array,
      default: () => []
    },
    pickingScannedArticles: {
      type: Object,
      default: () => ({})
    },
    packingScannedArticles: {
      type: Object,
      default: () => ({})
    },
    currentFlyers: {
      type: Array,
      default: () => []
    },
    upsellItems: {
      type: Array,
      default: () => []
    },
    packaging: {
      type: Object,
      default: null
    },
    swissDocumentUrls: {
      type: Array,
      default: () => []
    },
    shippingLabelUrls: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    currentUpsellItem() {
      if (!this.upsellItems || this.upsellItems.length === 0) {
        return null;
      }
      return this.upsellItems[this.currentUpsellIndex] || this.upsellItems[0];
    },
    currentFlyerItem() {
      if (!this.currentFlyers || this.currentFlyers.length === 0) {
        return null;
      }
      return this.currentFlyers[this.currentFlyerIndex] || this.currentFlyers[0];
    },
    currentArticle() {
      // Find the first article that hasn't been fully scanned yet in picking phase
      if (!this.articles || this.articles.length === 0) return null;

      for (const article of this.articles) {
        if (!this.isArticleScannedInPicking(article)) {
          return article;
        }
      }

      // If all are scanned, return the last one
      return this.articles[this.articles.length - 1];
    },

    currentArticleForPacking() {
      // Find the first article that hasn't been fully scanned yet in packing phase
      if (!this.articles || this.articles.length === 0) return null;

      for (const article of this.articles) {
        if (!this.isArticleScannedInPacking(article)) {
          return article;
        }
      }

      // If all are scanned, return the last one
      return this.articles[this.articles.length - 1];
    },

    // Image computed properties for reactive updates
    swissLabelImage() {
      return require('@/assets/files/SwissLabel.png');
    },

    shippingLabelImage() {
      return require('@/assets/files/ShippingLabel.png');
    },

    // Computed property to determine current label type for Step 7
    currentLabelType() {
      if (this.step === 7) {
        // For Swiss orders: show Swiss label first, then shipping label after first scan
        if (this.swissDocumentUrls && this.swissDocumentUrls.length > 0) {
          // Check if at least one tracking ID has been scanned (Swiss document)
          const hasScannedOne = this.scannedTrackingIds && this.scannedTrackingIds.length > 0;
          return hasScannedOne ? 'shipping' : 'swiss';
        }
        // For non-Swiss orders: always show shipping label
        return 'shipping';
      } else if (this.step === 8) {
        return 'shipping';
      }
      return null;
    }
  },
  methods: {
    // Status bar helper methods
    getStatusBarClass() {
      switch(this.step) {
        case 1: return 'picking';
        case 2: return 'walking';
        case 3: return 'packing';
        case 4: return 'carton';
        case 5: return 'upsell';
        case 6: return 'flyer';
        case 7: return 'label-scan';
        case 8: return 'additional-label';
        default: return '';
      }
    },

    getStatusIcon() {
      switch(this.step) {
        case 1: return 'mdi-hand-back-right';
        case 2: return 'mdi-walk';
        case 3: return 'mdi-package-variant';
        case 4: return 'mdi-package-variant-closed';
        case 5: return 'mdi-gift';
        case 6: return 'mdi-file-document';
        case 7: return 'mdi-barcode-scan';
        case 8: return 'mdi-barcode-scan';
        default: return 'mdi-help';
      }
    },

    getStatusText() {
      switch(this.step) {
        case 1: return 'Picken';
        case 2: return 'Packtisch';
        case 3: return 'Packen';
        case 4: return 'Karton';
        case 5: return 'Upsells';
        case 6: return 'Flyers';
        case 7: return 'Label Scan';
        case 8: return 'Zweites Label';
        default: return '';
      }
    },

    getStatusProgress() {
      switch(this.step) {
        case 1:
          return `${this.progress.scanned} / ${this.progress.total}`;
        case 3:
          return `${this.packingProgress.scanned} / ${this.packingProgress.total}`;
        case 5:
          if (this.upsellItems && this.upsellItems.length > 0) {
            return `${this.currentUpsellIndex + 1} / ${this.upsellItems.length}`;
          }
          return null;
        case 6:
          if (this.currentFlyers && this.currentFlyers.length > 0) {
            return `${this.currentFlyerIndex + 1} / ${this.currentFlyers.length}`;
          }
          return null;
        case 8:
          if (this.remainingTrackingIds && this.remainingTrackingIds.length > 0) {
            return `${this.remainingTrackingIds.length} übrig`;
          }
          return null;
        default:
          return null;
      }
    },

    addDebugLog(message, type = 'info') {
      const now = new Date();
      const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
      this.debugLogs.push({ time, message, type });
      // Keep only last 50 logs
      if (this.debugLogs.length > 50) {
        this.debugLogs.shift();
      }
    },

    toggleDebugConsole() {
      this.debugConsoleExpanded = !this.debugConsoleExpanded;
    },

    enableDebugMode() {
      this.debugModeActive = true;
      this.debugConsoleExpanded = true;
      this.debugLogs = []; // Clear old logs
      this.addDebugLog('🐛 Debug mode ACTIVATED', 'success');
      this.addDebugLog('Admin key scanned - debug enabled', 'info');
      console.log('🐛 [MobileWorkflowView] Debug mode activated');
    },

    disableDebugMode() {
      this.addDebugLog('🐛 Debug mode DISABLED', 'warning');
      console.log('🐛 [MobileWorkflowView] Debug mode disabled');
      this.debugModeActive = false;
      this.debugConsoleExpanded = false;
      this.debugLogs = [];
    },

    getArticleKey(article) {
      return `${article.sku}_${article.shelfHashcode || article.hashcode}`;
    },

    isArticleScannedInPicking(article) {
      const key = this.getArticleKey(article);
      const scannedCount = this.pickingScannedArticles[key] || 0;
      return scannedCount >= (article.qty || 1);
    },

    isArticleScannedInPacking(article) {
      const key = this.getArticleKey(article);
      const scannedCount = this.packingScannedArticles[key] || 0;
      return scannedCount >= (article.qty || 1);
    },

    getPickingScannedCount(article) {
      const key = this.getArticleKey(article);
      return this.pickingScannedArticles[key] || 0;
    },

    getPackingScannedCount(article) {
      const key = this.getArticleKey(article);
      return this.packingScannedArticles[key] || 0;
    },

    getImageUrl(product) {
      // Get image URL from product - same logic as ArticleCard
      const hasDesignId = product.designId || product.attributes?.designId;
      const hasPreview = product.attributes?.preview;

      if (hasDesignId) {
        return `https://accelery-poster.s3.eu-central-1.amazonaws.com/${encodeURIComponent(hasDesignId)}.png`;
      } else if (hasPreview) {
        return hasPreview[0]?.url || hasPreview;
      } else {
        // Search for any HTTP URL in attributes
        let foundImageUrl = null;
        for (const [key, value] of Object.entries(product.attributes || {})) {
          if (typeof value === 'string' && value.startsWith('http')) {
            foundImageUrl = value;
            break;
          }
        }
        return foundImageUrl;
      }
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      console.log('🌓 Dark mode toggled:', this.isDarkMode ? 'ON' : 'OFF');
    },

    handleTouchStart(event) {
      this.touchStartY = event.touches[0].clientY;
    },

    handleTouchMove(event) {
      const currentY = event.touches[0].clientY;
      this.touchDelta = currentY - this.touchStartY;
    },

    handleTouchEnd() {
      // Show toolbar if user swiped down more than 50px
      if (this.touchDelta > 50) {
        this.showToolbar = true;
        // Auto-hide after 3 seconds
        setTimeout(() => {
          this.showToolbar = false;
        }, 3000);
      }
      // Reset
      this.touchStartY = 0;
      this.touchDelta = 0;
    },

    handleUpsellConfirm() {
      console.log('[Mobile Workflow] Upsell confirmed, index:', this.currentUpsellIndex);

      // Check if there are more upsells to show
      if (this.currentUpsellIndex < this.upsellItems.length - 1) {
        // Move to next upsell
        this.currentUpsellIndex++;
        console.log('[Mobile Workflow] Moving to next upsell, new index:', this.currentUpsellIndex);
      } else {
        // All upsells confirmed, emit event
        console.log('[Mobile Workflow] All upsells confirmed');
        this.currentUpsellIndex = 0; // Reset for next order
        this.$emit('confirm-upsells');
      }
    },

    handleFlyerConfirm() {
      console.log('[Mobile Workflow] Flyer confirmed, index:', this.currentFlyerIndex);

      // Check if there are more flyers to show
      if (this.currentFlyerIndex < this.currentFlyers.length - 1) {
        // Move to next flyer
        this.currentFlyerIndex++;
        console.log('[Mobile Workflow] Moving to next flyer, new index:', this.currentFlyerIndex);
      } else {
        // All flyers confirmed, emit event
        console.log('[Mobile Workflow] All flyers confirmed');
        this.currentFlyerIndex = 0; // Reset for next order
        this.$emit('confirm-flyer');
      }
    },

    handleReprintLabel() {
      console.log('[Mobile Workflow] Reprint requested for step', this.step);

      // For Step 7: Check if we need to reprint Swiss documents or shipping label
      if (this.swissDocumentUrls && this.swissDocumentUrls.length > 0) {
        // Reprint Swiss A4 documents
        console.log('[Mobile Workflow] Reprinting Swiss A4 documents');
        this.$emit('reprint-swiss-documents');
      } else {
        // Reprint shipping label (non-Swiss orders)
        console.log('[Mobile Workflow] Reprinting shipping label');
        this.$emit('reprint-shipping-label');
      }
    },

    handleReprintShippingLabel() {
      console.log('[Mobile Workflow] Reprint shipping label requested for Step 8');
      // For Step 8: Always reprint shipping label
      this.$emit('reprint-shipping-label');
    },

    getUpsellImageUrl(sku) {
      // Return API endpoint for upsell item images
      return `/api/files/sku?sku=${sku}`;
    },

    // Fullscreen methods
    checkFullscreenSupport() {
      this.fullscreenSupported = !!(
        document.fullscreenEnabled ||
        document.webkitFullscreenEnabled ||
        document.mozFullScreenEnabled ||
        document.msFullscreenEnabled
      );
    },

    async enterFullscreen() {
      if (!this.fullscreenSupported) {
        console.warn('[Mobile Workflow] Fullscreen not supported');
        return;
      }

      try {
        const element = document.documentElement;

        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }

        console.log('[Mobile Workflow] Entered fullscreen mode');
      } catch (error) {
        console.error('[Mobile Workflow] Error entering fullscreen:', error);
      }
    },

    async exitFullscreen() {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }

        console.log('[Mobile Workflow] Exited fullscreen mode');
      } catch (error) {
        console.error('[Mobile Workflow] Error exiting fullscreen:', error);
      }
    },

    handleFullscreenChange() {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      this.isFullscreen = isFullscreen;
      console.log('[Mobile Workflow] Fullscreen state changed:', isFullscreen);
    },

  },
  mounted() {
    // Initialize fullscreen support
    this.checkFullscreenSupport();

    // Add fullscreen event listeners
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    // Remove fullscreen event listeners
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);

    // Exit fullscreen if active
    if (this.isFullscreen) {
      this.exitFullscreen();
    }
  },
  watch: {
    step(newStep) {
      // Reset indices when entering respective steps
      if (newStep === 5) {
        this.currentUpsellIndex = 0;
        console.log('[Mobile Workflow] Entering upsell step, reset index to 0');
      } else if (newStep === 6) {
        this.currentFlyerIndex = 0;
        console.log('[Mobile Workflow] Entering flyer step, reset index to 0');
      }
    }
  }
};
</script>

<style scoped>
.mobile-workflow-container {
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile browsers */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  touch-action: pan-y;
}

/* Back Button (Top Left - below status bar) */
.back-button {
  position: fixed;
  top: 64px; /* Below status bar (44px height + 20px padding) */
  left: 16px;
  z-index: 1000;
}

/* Fullscreen Toggle (Below Back Button) */
.fullscreen-toggle {
  position: fixed;
  top: 118px; /* Back button top + 40px button + 14px gap */
  left: 16px;
  z-index: 1000;
}

/* Printer Button (Below Fullscreen) */
.printer-button {
  position: fixed;
  top: 172px; /* Fullscreen top + 40px button + 14px gap */
  left: 16px;
  z-index: 1000;
}

/* Problem Button (Below Printer) - Only visible during picking */
.problem-button {
  position: fixed;
  top: 226px; /* Printer top + 40px button + 14px gap */
  left: 16px;
  z-index: 1000;
}

/* Full-Width Status Bar */
.status-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999; /* Below FAB buttons (1000) but above content */
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-bar.picking {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.status-bar.walking {
  background: linear-gradient(135deg, #757575 0%, #616161 100%);
}

.status-bar.packing {
  background: linear-gradient(135deg, #43a047 0%, #388e3c 100%);
}

.status-bar.carton {
  background: linear-gradient(135deg, #f57c00 0%, #ef6c00 100%);
}

.status-bar.upsell {
  background: linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%);
}

.status-bar.flyer {
  background: linear-gradient(135deg, #00897b 0%, #00796b 100%);
}

.status-bar.label-scan {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%);
}

.status-bar.additional-label {
  background: linear-gradient(135deg, #ff5722 0%, #e64a19 100%);
}

.status-bar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.status-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-icon {
  flex-shrink: 0;
}

.status-text {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.status-slot {
  font-size: 16px;
  font-weight: 500;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.status-progress {
  display: flex;
  align-items: center;
}

.progress-text {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Scan Debug Badge */
.scan-debug-badge {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1001;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Order ID Badge */
.order-id-badge {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 1000;
}

.order-chip {
  font-size: 13px !important;
  font-weight: 600 !important;
  padding: 6px 10px !important;
  height: 32px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}

.order-id-text {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Slot Info */
.slot-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background: #e3f2fd;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
  color: #1976d2;
  align-self: center;
}

.workflow-step {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 480px;
}

/* Minimal Header */
.minimal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-label {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.slot-badge {
  font-size: 14px;
  font-weight: 600;
  color: #1976d2;
  background: #e3f2fd;
  padding: 4px 12px;
  border-radius: 12px;
}

.progress-counter {
  font-size: 18px;
  font-weight: 700;
  color: #1976d2;
}

/* Article Card - Matches Desktop Style */
.article-card {
  background: #ffffff;
  border: 3px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s ease;
}

.article-card.scanned {
  background: #e8f5e9;
  border-color: #4caf50;
}

.article-sku {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  text-align: center;
  word-break: break-word;
}

.article-preview {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fafafa;
  border-radius: 8px;
  padding: 12px;
  height: 200px;
  position: relative;
  cursor: pointer;
}

.article-preview img {
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
}

/* Dark Mode Styles */
.article-card.dark-mode .article-preview {
  background: #000;
}

.article-card.dark-mode .article-preview.no-image {
  background: #000;
}

/* Dark Mode Indicator */
.dark-mode-indicator {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background-color: rgba(33, 33, 33, 0.9);
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: white;
}

/* No Image / Text Fallback */
.article-preview.no-image {
  background: #f5f5f5;
}

.article-text-fallback {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: center;
  padding: 20px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.article-card.dark-mode .article-text-fallback {
  color: #fff;
}

/* Transition Messages */
.transition-message,
.scan-message,
.complete-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 40px 20px;
  text-align: center;
}

.transition-message p,
.scan-message p,
.complete-message p {
  font-size: 18px;
  font-weight: 500;
  color: #666;
  margin: 0;
}

/* ========================================
   CARTON DISPLAY - LARGE (FIT VIEWPORT)
   ======================================== */
.carton-step {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
  padding-top: 60px; /* Space for status bar */
}

.carton-display-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #795548;
  min-height: 0;
  overflow: hidden;
}

.carton-large-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 0;
}

.carton-image-wrapper {
  flex: 1;
  width: 100%;
  max-width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 0;
}

.carton-large-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.carton-large-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
}

.carton-large-name {
  font-size: 18px;
  font-weight: 700;
  color: #795548;
}

.carton-large-sku {
  font-size: 14px;
  font-weight: 600;
  color: #795548;
  background: rgba(121, 85, 72, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
}

.carton-large-dimensions {
  font-size: 12px;
  color: #666;
}

.carton-large-info {
  flex-shrink: 0;
}

.carton-step .confirm-btn {
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  min-height: 56px !important;
  max-height: 56px !important;
}

/* ========================================
   UPSELL STEP - FIT VIEWPORT
   ======================================== */
.upsell-step {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
  padding-top: 60px; /* Space for status bar */
}

.upsell-display-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  border-radius: 12px;
  border: 2px solid #9b59b6;
  min-height: 0;
  overflow: hidden;
}

.upsell-large-card {
  position: relative;
  flex: 1;
  width: 100%;
  max-width: 300px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(155, 89, 182, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
  min-height: 0;
}

.upsell-large-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.qty-badge-large {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #f44336;
  color: white;
  font-size: 18px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 6px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.upsell-large-badge {
  flex-shrink: 0;
  background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(155, 89, 182, 0.4);
}

.upsell-step .confirm-btn {
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  min-height: 56px !important;
  max-height: 56px !important;
}

/* ========================================
   FLYER STEP - FIT VIEWPORT
   ======================================== */
.flyer-step-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
  padding-top: 60px; /* Space for status bar */
}

.flyer-display-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  border: 2px solid #1976d2;
  min-height: 0;
  overflow: hidden;
}

.flyer-large-card {
  flex: 1;
  width: 100%;
  max-width: 300px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 12px;
  min-height: 0;
}

.flyer-large-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.flyer-large-badge {
  flex-shrink: 0;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

.flyer-step-container .confirm-flyer-btn {
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  min-height: 56px !important;
  max-height: 56px !important;
}

.label-scan-step {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  padding-top: 60px; /* Space for status bar */
  gap: 24px;
}

.reprint-label-btn {
  margin-top: 20px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
}

.additional-label-step {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  padding-top: 60px; /* Space for status bar */
}

/* Confirm Buttons */
.confirm-btn,
.confirm-flyer-btn {
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  height: 56px !important;
  min-height: 56px !important;
  max-height: 56px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
}

/* Walk to Table Step - Fit entire viewport without scrolling */
.walk-to-table-step {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  gap: 0;
}

.walk-image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 8px;
  min-height: 0; /* Important for flex child to shrink */
}

.walk-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
}

.walk-content {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.walk-text {
  font-size: 18px;
  font-weight: 500;
  color: #666;
  text-align: center;
  margin: 0;
}

.walk-button {
  height: 56px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
}

/* Debug Console Panel */
.debug-console-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.95);
  color: #00ff00;
  font-family: monospace;
  font-size: 11px;
  max-height: 300px;
  overflow: hidden;
}

.debug-console-header {
  padding: 8px 12px;
  background: #1a1a1a;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 2px solid #333;
}

.debug-console-content {
  max-height: 250px;
  overflow-y: auto;
  padding: 8px;
}

.debug-log-entry {
  padding: 4px 0;
  border-bottom: 1px solid #333;
  display: flex;
  gap: 8px;
}

.debug-log-entry.success {
  color: #4caf50;
}

.debug-log-entry.warning {
  color: #ff9800;
}

.debug-log-entry.error {
  color: #f44336;
}

.log-time {
  color: #666;
  flex-shrink: 0;
}

.log-message {
  word-break: break-all;
}

/* Label Background Styles - Full Screen */
.label-background-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  overflow: auto;
  z-index: 1;
}

.label-background-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

/* Specific styling for A4 Swiss label (portrait) */
.label-image-a4 {
  width: auto;
  max-width: 90%;
  height: auto;
  max-height: 100%;
}

/* Specific styling for shipping label (landscape) */
.label-image-shipping {
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 90%;
}

/* Controls Overlay at Bottom */
.label-controls-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.7) 80%, transparent 100%);
  padding: 30px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  z-index: 2;
}

.label-scan-step,
.additional-label-step {
  position: relative;
  height: 100%;
}

.label-controls-overlay .scan-message {
  text-align: center;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.label-controls-overlay .scan-message p {
  font-size: 18px;
  font-weight: 600;
  margin-top: 8px;
  margin-bottom: 0;
}

.label-controls-overlay .reprint-label-btn {
  background: white !important;
  min-width: 280px;
}
</style>
